<script lang="ts">
  import { onMount } from "svelte";
  import { api } from "../lib/api";

  let users = $state<{ username: string; createdAt: string }[]>([]);
  let loading = $state(true);
  let errorMsg = $state("");
  let successMsg = $state("");

  // Add user form
  let newUsername = $state("");
  let newPassword = $state("");
  let adding = $state(false);

  // Reset password
  let resetTarget = $state<string | null>(null);
  let resetPassword = $state("");

  // Shared token
  let sharedToken = $state("");
  let savingToken = $state(false);
  let tokenConfigured = $state<boolean | null>(null);

  onMount(async () => {
    await Promise.all([loadUsers(), checkTokenStatus()]);
  });

  async function loadUsers() {
    loading = true;
    errorMsg = "";
    try {
      const res = await api.listUsers();
      users = res.users;
    } catch (err: any) {
      errorMsg = err.message;
    } finally {
      loading = false;
    }
  }

  async function addUser() {
    if (!newUsername.trim() || !newPassword) return;
    adding = true;
    errorMsg = "";
    successMsg = "";
    try {
      await api.addUser(newUsername.trim(), newPassword);
      successMsg = `User "${newUsername.trim()}" created`;
      newUsername = "";
      newPassword = "";
      await loadUsers();
    } catch (err: any) {
      errorMsg = err.message;
    } finally {
      adding = false;
    }
  }

  async function removeUser(username: string) {
    if (!confirm(`Remove user "${username}"?`)) return;
    errorMsg = "";
    successMsg = "";
    try {
      await api.removeUser(username);
      successMsg = `User "${username}" removed`;
      await loadUsers();
    } catch (err: any) {
      errorMsg = err.message;
    }
  }

  async function doResetPassword() {
    if (!resetTarget || !resetPassword) return;
    errorMsg = "";
    successMsg = "";
    try {
      await api.resetUserPassword(resetTarget, resetPassword);
      successMsg = `Password reset for "${resetTarget}"`;
      resetTarget = null;
      resetPassword = "";
    } catch (err: any) {
      errorMsg = err.message;
    }
  }

  async function checkTokenStatus() {
    try {
      const res = await api.getSharedTokenStatus();
      tokenConfigured = res.configured;
    } catch {
      tokenConfigured = null;
    }
  }

  async function saveSharedToken() {
    if (!sharedToken.trim()) return;
    savingToken = true;
    errorMsg = "";
    successMsg = "";
    try {
      await api.setSharedToken(sharedToken.trim());
      successMsg = "Shared token updated";
      sharedToken = "";
      tokenConfigured = true;
    } catch (err: any) {
      errorMsg = err.message;
    } finally {
      savingToken = false;
    }
  }
</script>

<div class="admin">
  <div class="admin-header">
    <a href="#/" class="back">&larr; Dashboard</a>
    <h2>User Management</h2>
    <p class="admin-subtitle">Manage password-based login users. Only token-authenticated admins can access this page.</p>
  </div>

  {#if errorMsg}
    <div class="banner error-banner">{errorMsg}</div>
  {/if}
  {#if successMsg}
    <div class="banner success-banner">{successMsg}</div>
  {/if}

  <!-- Shared token -->
  <section class="section" class:section-warn={tokenConfigured === false}>
    <div class="section-header">
      <h3>Shared ServiceAccount Token</h3>
      {#if tokenConfigured === true}
        <span class="token-status configured">Configured</span>
      {:else if tokenConfigured === false}
        <span class="token-status not-configured">Not configured</span>
      {/if}
    </div>
    <p class="section-desc">
      All password users operate under this K8s identity. Paste a ServiceAccount bearer token.
      {#if tokenConfigured === false}
        <strong>Password login will not work until this is set.</strong>
      {/if}
    </p>
    <div class="inline-form">
      <input
        type="password"
        bind:value={sharedToken}
        placeholder="Paste ServiceAccount token..."
        class="input wide"
      />
      <button class="btn-primary" onclick={saveSharedToken} disabled={!sharedToken.trim() || savingToken}>
        {savingToken ? "Saving..." : tokenConfigured ? "Update" : "Save"}
      </button>
    </div>
  </section>

  <!-- Users list -->
  <section class="section">
    <h3>Users</h3>

    {#if loading}
      <p class="muted">Loading...</p>
    {:else if users.length === 0}
      <p class="muted">No password users configured yet.</p>
    {:else}
      <div class="user-list">
        {#each users as user (user.username)}
          <div class="user-row">
            <span class="user-name">{user.username}</span>
            <span class="user-date">Created {new Date(user.createdAt).toLocaleDateString()}</span>
            <div class="user-actions">
              <button class="btn-small" onclick={() => { resetTarget = user.username; resetPassword = ""; }}>
                Reset password
              </button>
              <button class="btn-small btn-danger" onclick={() => removeUser(user.username)}>
                Remove
              </button>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Reset password inline -->
    {#if resetTarget}
      <div class="inline-form reset-form">
        <span class="reset-label">New password for <strong>{resetTarget}</strong>:</span>
        <input
          type="password"
          bind:value={resetPassword}
          placeholder="New password (min 6 chars)"
          class="input"
        />
        <button class="btn-primary" onclick={doResetPassword} disabled={resetPassword.length < 6}>
          Reset
        </button>
        <button class="btn-ghost" onclick={() => { resetTarget = null; }}>Cancel</button>
      </div>
    {/if}
  </section>

  <!-- Add user -->
  <section class="section">
    <h3>Add User</h3>
    <div class="add-form">
      <input
        type="text"
        bind:value={newUsername}
        placeholder="Username"
        class="input"
        autocomplete="off"
      />
      <input
        type="password"
        bind:value={newPassword}
        placeholder="Password (min 6 chars)"
        class="input"
        autocomplete="new-password"
      />
      <button class="btn-primary" onclick={addUser} disabled={!newUsername.trim() || newPassword.length < 6 || adding}>
        {adding ? "Adding..." : "Add user"}
      </button>
    </div>
  </section>
</div>

<style>
  .admin {
    max-width: 700px;
  }

  .admin-header {
    margin-bottom: 1.5rem;
  }

  .back {
    color: var(--text-muted);
    text-decoration: none;
    font-size: 0.8rem;
  }

  .back:hover { color: var(--accent); }

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 0.25rem;
  }

  .admin-subtitle {
    color: var(--text-muted);
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .banner {
    padding: 0.6rem 1rem;
    border-radius: 4px;
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }

  .error-banner {
    background: rgba(248, 113, 113, 0.1);
    border: 1px solid var(--danger);
    color: var(--danger);
  }

  .success-banner {
    background: rgba(74, 222, 128, 0.1);
    border: 1px solid var(--success);
    color: var(--success);
  }

  .section {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 1.25rem;
    margin-bottom: 1rem;
  }

  .section.section-warn {
    border-color: var(--warning);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.25rem;
  }

  .token-status {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
    font-weight: 600;
  }

  .token-status.configured {
    background: rgba(74, 222, 128, 0.15);
    color: var(--success);
  }

  .token-status.not-configured {
    background: rgba(251, 191, 36, 0.15);
    color: var(--warning);
  }

  h3 {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 0;
  }

  .section-desc {
    color: var(--text-muted);
    font-size: 0.8rem;
    margin-bottom: 0.75rem;
  }

  .muted {
    color: var(--text-muted);
    font-size: 0.85rem;
  }

  .input {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 4px;
    color: var(--text);
    padding: 0.45rem 0.6rem;
    font-size: 0.85rem;
  }

  .input:focus { outline: none; border-color: var(--accent); }
  .input.wide { flex: 1; }

  .inline-form {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .add-form {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .reset-form {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--border);
  }

  .reset-label {
    font-size: 0.8rem;
    color: var(--text-muted);
    white-space: nowrap;
  }

  .user-list {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .user-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border);
  }

  .user-row:last-child { border-bottom: none; }

  .user-name {
    font-family: var(--font-mono);
    font-weight: 600;
    font-size: 0.85rem;
  }

  .user-date {
    color: var(--text-muted);
    font-size: 0.75rem;
  }

  .user-actions {
    margin-left: auto;
    display: flex;
    gap: 0.4rem;
  }

  .btn-primary {
    padding: 0.4rem 0.75rem;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8rem;
    white-space: nowrap;
  }

  .btn-primary:hover:not(:disabled) { background: var(--accent-hover); }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-small {
    padding: 0.25rem 0.5rem;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 3px;
    color: var(--text);
    cursor: pointer;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .btn-small:hover { border-color: var(--text-muted); }
  .btn-small.btn-danger { color: var(--danger); border-color: var(--danger); }
  .btn-small.btn-danger:hover { background: rgba(248, 113, 113, 0.1); }

  .btn-ghost {
    background: none;
    border: 1px solid var(--border);
    color: var(--text-muted);
    padding: 0.4rem 0.75rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8rem;
  }

  .btn-ghost:hover { color: var(--text); }
</style>
